import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { capitalize } from "lodash";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";

import Widget from "../../../../forms/components/Widget";
import { FAVORITE_QUERIES_KEY } from "../../../../hooks";
import { NotificationsLevel } from "../../../../lib/enums";
import api from "../../../../services/api";
import { show } from "../../../../notifications/notifications";
import { isNotAllowedNginx } from "../../../../utils";
import {
  prepareQueryAndSelectionsQueryParams,
  removeLabelFromSearchSelections
} from "../../util";
import {
  IAddFavoriteErrorResponse,
  IAddFavouriteProps
} from "./@types/AddFavoriteModal";
import { ErrorResponse } from "../../../../requests/@types";

const AddFavourite = ({
  formatErrorMessage = capitalize,
  onClose,
  searchValue
}: IAddFavouriteProps) => {
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<Array<string> | undefined>(undefined);

  const handleAddFavourite: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    const { value: name } = e.currentTarget["add-favourite"] || {};

    setErrors(undefined);
    return api
      .addFavourite({
        ...prepareQueryAndSelectionsQueryParams(
          searchValue.q || "",
          removeLabelFromSearchSelections(searchValue.selections || {})
        ),
        location: searchValue.location,
        name
      })
      .then(() => {
        show({
          level: NotificationsLevel.SUCCESS,
          message: `Favorite query '${name}' created.`
        });
        queryClient.invalidateQueries({ queryKey: FAVORITE_QUERIES_KEY });
        onClose();
      })
      .catch((err: ErrorResponse<IAddFavoriteErrorResponse>) => {
        if (
          isNotAllowedNginx({
            headers: { server: err.headers?.server },
            status: err.status
          })
        ) {
          show({
            level: NotificationsLevel.ERROR,
            message:
              "Configuration in process. Changes to Favorite Queries are not allowed during that time."
          });
          onClose();
          return;
        }

        if (typeof err.data === "string") {
          show({
            level: NotificationsLevel.ERROR,
            message: err.data
          });
          onClose();
          return;
        }

        const { name: favoriteName, query, non_field_errors } = err.data || {};

        if (favoriteName?.length) {
          setErrors(favoriteName.map(item => formatErrorMessage(item)));
        } else if (query?.length || non_field_errors?.length) {
          show({
            level: NotificationsLevel.ERROR,
            message: query || non_field_errors
          });
          onClose();
        } else {
          show({
            level: NotificationsLevel.ERROR,
            message: `Oops! Error while adding favorite "${name}" query. Please try again.`
          });
          onClose();
        }
      });
  };

  return (
    <Modal show size="lg" className="add-favourite-modal" onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Add Favorite</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleAddFavourite}>
        <Modal.Body>
          <Widget
            name="add-favourite"
            label="Name"
            errors={errors}
            onChange={() => null}
            autoFocus
          />
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="prompt-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="prompt-primary">
            Add
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddFavourite;
